// src/pages/PackReaderLiterature.jsx
//
// Literature-study student reader. As of slice skills-A (D3-B) this is a THIN
// WRAPPER over the shared PackReaderGrouped: it supplies the literature grouping
// (lit_section items grouped by sectionKind via SECTION_ORDER) and the per-kind
// section renderers. Behaviour is IDENTICAL to the previous standalone reader --
// nav, multi-item sub-lists, practice (quiz + essays + flash cards), entitlement
// gating, locked CTA, AI/Notes drawers and StartHere are all unchanged (they live in
// the shared reader). Flashcards practice IS enabled (enableFlashcards: true) --
// restores the prototype's literature flash cards; the shared reader builds the entry.
import React from 'react';
import PackReaderGrouped from './PackReaderGrouped.jsx';
import { groupByFixedOrder } from './readerGrouping.js';
import LitOverviewSection from './LitOverviewSection.jsx';
import LitActSection from './LitActSection.jsx';
import LitCharacterSection from './LitCharacterSection.jsx';
import LitThemeSection from './LitThemeSection.jsx';
import LitSymbolSection from './LitSymbolSection.jsx';
import LitQuotesSection from './LitQuotesSection.jsx';
import LitEssaySection from './LitEssaySection.jsx';
import LitMatrixSection from './LitMatrixSection.jsx';

// Section order + labels (student view, mirrors LIT_SIDEBAR_GROUPS).
const SECTION_ORDER = [
    { key: 'overview', label: 'Overview' },
    { key: 'act', label: 'Acts & Scenes' },
    { key: 'character', label: 'Characters' },
    { key: 'theme', label: 'Themes' },
    { key: 'symbol', label: 'Symbols' },
    { key: 'essay', label: 'Sample Essays' },
    { key: 'quotes', label: 'Quote Bank' },
    { key: 'matrix', label: 'Theme Matrix' },
];

function selectLitSections(items) {
    return (items || []).filter((it) => it && it.type === 'lit_section' && it.content && it.content.sectionKind);
}

// Render one lit_section by its sectionKind (the unchanged per-kind switch; unknown
// kinds fall back to the "next slice" placeholder, labelled by the active group).
function renderLitSection(item, group, done) {
    const kind = item.content.sectionKind;
    if (kind === 'overview') return <LitOverviewSection content={item.content} done={done} />;
    if (kind === 'act') return <LitActSection content={item.content} done={done} />;
    if (kind === 'character') return <LitCharacterSection content={item.content} done={done} />;
    if (kind === 'theme') return <LitThemeSection content={item.content} done={done} />;
    if (kind === 'symbol') return <LitSymbolSection content={item.content} done={done} />;
    if (kind === 'quotes') return <LitQuotesSection content={item.content} done={done} />;
    if (kind === 'essay') return <LitEssaySection content={item.content} done={done} />;
    if (kind === 'matrix') return <LitMatrixSection content={item.content} done={done} />;
    return (
        <div className="border border-book-accent/20 bg-book-page/30 p-10 text-center">
            <div className="font-display text-book-accent text-xs mb-3 font-semibold">{group.label}</div>
            <p className="font-serif italic text-book-muted">This section renders in the next slice.</p>
        </div>
    );
}

const LIT_CONFIG = {
    headerLabel: 'Literature Study',
    itemType: 'lit_section',
    enableFlashcards: true,
    selectSections: selectLitSections,
    buildGroups: (sections) => groupByFixedOrder(sections, (it) => it.content.sectionKind, SECTION_ORDER),
    groupKeyOf: (item) => item.content.sectionKind,
    subListLabel: (item) => (item.content && item.content.title) || item.id,
    renderSection: renderLitSection,
};

export default function PackReaderLiterature() {
    return <PackReaderGrouped config={LIT_CONFIG} />;
}
